import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { trackMiniAppEvent } from '../../utils/analytics';
import {
  getEngineRootStyle,
  titleStyle,
  instructionsStyle,
  primaryBtn,
  secondaryBtn,
  feedbackCorrect,
  feedbackIncorrect,
} from './shared/engineTheme';
import { useCelebration, CelebrationOverlay, A11yAnnouncement } from './shared/Celebration';
import { useGuidedHints } from './shared/useGuidedHints';
import GuidedHintOverlay, {
  GuidedHintHelpButton,
  guidedHintReadAloud,
} from './shared/GuidedHintOverlay';
import { SWITCH_LOGIC_ONE_SHOT } from './shared/engineInteractionOneShots';
import { isTapFirstLessonUi } from './shared/lessonUiMode';
import { shouldSuppressActivityTitle } from './shared/guidedPromptUtils';

/**
 * Switch logic playground — toggles + Check against a target state / lamp rule.
 *
 * Content:
 * {
 *   instructions?, question?,
 *   switches: [{ id, label }],
 *   logic: { type: 'AND'|'OR'|'SERIES'|'PARALLEL', requiredOn?: string[] },
 *   output: { type: 'bulb'|'motor'|'robot' },
 *   correctState: Record<switchId, boolean>,
 *   victory?, explanation?,
 * }
 *
 * SERIES is treated like AND (all closed). PARALLEL is treated like OR (any closed).
 * Guided shell + footer Check: for **AND / SERIES**, the output panel stays “dark” until Check
 * (or Show answer) even when the combo would light under the rule — OR / PARALLEL keeps a live preview.
 */

function evalLogic(logic, states, switchIds) {
  const t = (logic?.type || 'AND').toUpperCase();
  const onList = switchIds.filter((id) => states[id]);
  if (t === 'OR' || t === 'PARALLEL') {
    return onList.length > 0;
  }
  return switchIds.length > 0 && switchIds.every((id) => states[id]);
}

function outputEmoji(kind) {
  if (kind === 'motor') return '⚙️';
  if (kind === 'robot') return '🤖';
  return '💡';
}

export default function SwitchLogicEngine({
  title = 'Switch logic',
  content = {},
  hints = [],
  explanation,
  activityId = 'engine_switch_logic',
  onProgress,
  onComplete,
  guidedHintsEnabled = true,
  lessonUiMode = 'default',
  guidedContext,
  onRegisterLessonFooterActions,
}) {
  const singleSurface = guidedContext?.singleSurface === true;
  const stepTitle = typeof guidedContext?.stepTitle === 'string' ? guidedContext.stepTitle : '';
  const suppressActivityTitle = shouldSuppressActivityTitle({ stepTitle, activityTitle: title });
  const tapFirst = isTapFirstLessonUi(lessonUiMode);
  const compactPanel = tapFirst && singleSurface;

  const switches = Array.isArray(content.switches) ? content.switches : [];
  const logic = content.logic || { type: 'AND' };
  const output = content.output || { type: 'bulb' };
  const correctState = content.correctState && typeof content.correctState === 'object' ? content.correctState : {};

  const shellLessonDefer =
    typeof onRegisterLessonFooterActions === 'function' && switches.length > 0;

  const logicTypeUpper = (logic?.type || 'AND').toUpperCase();
  /** AND/SERIES: hide “lit” preview in lesson shell until Check so success isn’t spoiled early. OR/PARALLEL: live preview. */
  const deferOutputPreview =
    shellLessonDefer && (logicTypeUpper === 'AND' || logicTypeUpper === 'SERIES');

  const initial = useMemo(() => {
    const m = {};
    for (const sw of switches) {
      m[sw.id] = false;
    }
    return m;
  }, [switches]);

  const [states, setStates] = useState(() => ({ ...initial }));
  const [evalVisible, setEvalVisible] = useState(() => !shellLessonDefer);
  const [completed, setCompleted] = useState(false);
  const [showCelebration, celebrate] = useCelebration();
  const [a11y, setA11y] = useState('');
  const [wrong, setWrong] = useState(false);

  const switchIds = useMemo(() => switches.map((s) => s.id), [switches]);

  const lampOn = useMemo(() => evalLogic(logic, states, switchIds), [logic, states, switchIds]);

  const showEval = !shellLessonDefer || evalVisible;
  const displayLampOn = deferOutputPreview ? lampOn && (showEval || completed) : lampOn;

  useEffect(() => {
    setEvalVisible(!shellLessonDefer);
  }, [activityId, shellLessonDefer]);

  const getExplanation = useCallback(() => content.explanation, [content.explanation]);

  const guided = useGuidedHints({
    activityId,
    hints,
    oneShotInteractionHint: SWITCH_LOGIC_ONE_SHOT,
    explanation,
    getExplanation,
    isActivityComplete: completed,
    enabled: guidedHintsEnabled,
  });
  const { noteUserAction, noteCorrectAnswer, noteIncorrectAnswer, resetSession } = guided;

  const matchesCorrect = useMemo(() => {
    for (const id of switchIds) {
      if (!!states[id] !== !!correctState[id]) return false;
    }
    return switchIds.length > 0;
  }, [states, correctState, switchIds]);

  const runCheck = useCallback(() => {
    if (completed) return;
    noteUserAction();
    if (shellLessonDefer) setEvalVisible(true);
    if (matchesCorrect) {
      setCompleted(true);
      noteCorrectAnswer();
      celebrate();
      setA11y('That matches the challenge.');
      trackMiniAppEvent.completed(activityId);
      onComplete?.({ activityId, engine: 'switch_logic', perfect: true });
    } else {
      setWrong(true);
      noteIncorrectAnswer();
      setA11y('Try another combo.');
      trackMiniAppEvent.interaction(activityId, 'incorrect');
    }
  }, [
    completed,
    noteUserAction,
    shellLessonDefer,
    matchesCorrect,
    noteCorrectAnswer,
    celebrate,
    activityId,
    onComplete,
    noteIncorrectAnswer,
  ]);

  const revealAnswer = useCallback(() => {
    if (completed) return;
    noteUserAction();
    const next = { ...initial };
    for (const id of switchIds) {
      if (Object.prototype.hasOwnProperty.call(correctState, id)) next[id] = !!correctState[id];
    }
    setStates(next);
    setEvalVisible(true);
    setWrong(false);
  }, [completed, noteUserAction, initial, correctState, switchIds]);

  useEffect(() => {
    if (!onRegisterLessonFooterActions) return undefined;
    if (completed || !shellLessonDefer) {
      onRegisterLessonFooterActions(null);
      return undefined;
    }
    onRegisterLessonFooterActions({
      checkAnswer: runCheck,
      checkAnswerDisabled: false,
      showAnswer: revealAnswer,
    });
    return () => onRegisterLessonFooterActions(null);
  }, [onRegisterLessonFooterActions, completed, shellLessonDefer, runCheck, revealAnswer]);

  const toggle = (id) => {
    if (completed) return;
    noteUserAction();
    if (shellLessonDefer) {
      setEvalVisible(false);
      setWrong(false);
    }
    setStates((s) => ({ ...s, [id]: !s[id] }));
    onProgress?.({ activityId, engine: 'switch_logic', phase: 'toggle', id });
  };

  const resetAll = () => {
    if (completed) return;
    resetSession();
    setStates({ ...initial });
    setWrong(false);
    if (shellLessonDefer) setEvalVisible(false);
    onProgress?.({ activityId, engine: 'switch_logic', phase: 'reset' });
  };

  const q =
    content.question || 'Flip the switches, then tap Check when the output matches the goal.';

  const wrongStripVisible = wrong && !completed && (!shellLessonDefer || evalVisible);

  return (
    <div
      style={{
        ...getEngineRootStyle({ singleSurface }),
        position: 'relative',
        ...(guided.contentReserveStyle || {}),
        ...(compactPanel ? { overflow: 'hidden', minHeight: 0 } : {}),
      }}
    >
      <CelebrationOverlay visible={showCelebration} message={content.victory?.message || 'Logic unlocked!'} />
      <A11yAnnouncement message={a11y} />
      <GuidedHintOverlay
        open={guided.open}
        isNarrow={guided.isNarrow}
        visibleLevel={guided.visibleLevel}
        maxLevel={guided.maxLevel}
        isInteractionPhase={guided.isInteractionPhase}
        anotherClueEnabled={guided.anotherClueEnabled}
        hintEntry={guided.hintEntry}
        showHowAvailable={guided.showHowAvailable}
        resolvedExplanation={guided.resolvedExplanation}
        explanationOpen={guided.explanationOpen}
        onDismiss={guided.dismissOverlay}
        onAnotherClue={guided.anotherClue}
        onReadAloud={() =>
          guidedHintReadAloud(guided.hintEntry, activityId, guided.readAloudTrackLevel)
        }
        onShowHow={guided.openExplanation}
      />
      <GuidedHintHelpButton visible={guided.showHelpButton} onClick={guided.requestHintManually} />

      {!suppressActivityTitle ? (
        <h2 style={{ ...titleStyle, ...(compactPanel ? { margin: '0 0 6px' } : {}) }}>{title}</h2>
      ) : null}
      {compactPanel ? (
        <p
          style={{
            ...instructionsStyle,
            fontSize: guided.isNarrow ? 13 : 14,
            lineHeight: 1.35,
            margin: '0 0 8px',
            fontWeight: 600,
          }}
        >
          {q}
        </p>
      ) : (
        <>
          {content.instructions && <p style={instructionsStyle}>{content.instructions}</p>}
          <p
            style={{
              ...instructionsStyle,
              fontWeight: 700,
              marginTop: content.instructions ? undefined : 0,
            }}
          >
            {q}
          </p>
        </>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: compactPanel ? 6 : 10,
          marginBottom: compactPanel ? 8 : 14,
        }}
      >
        {switches.map((sw) => (
          <button
            key={sw.id}
            type="button"
            disabled={completed}
            onClick={() => toggle(sw.id)}
            aria-pressed={!!states[sw.id]}
            style={{
              ...secondaryBtn,
              flex: '1 1 140px',
              minHeight: compactPanel ? 44 : 48,
              borderWidth: states[sw.id] ? 3 : 2,
              borderColor: states[sw.id] ? 'var(--color-primary)' : 'var(--color-border)',
              background: states[sw.id] ? 'rgba(99,102,241,0.14)' : 'var(--color-bg)',
              fontWeight: 800,
              fontSize: compactPanel ? 14 : undefined,
            }}
          >
            {states[sw.id] ? 'ON' : 'OFF'} · {sw.label || sw.id}
          </button>
        ))}
      </div>

      <div
        style={{
          textAlign: 'center',
          padding: compactPanel ? '12px 10px' : '18px 12px',
          borderRadius: compactPanel ? 12 : 16,
          border: `2px solid ${displayLampOn ? 'rgba(250, 204, 21, 0.55)' : 'var(--color-border)'}`,
          background: displayLampOn ? 'rgba(250, 204, 21, 0.12)' : 'var(--color-bg)',
          marginBottom: compactPanel ? 8 : 12,
        }}
      >
        <div
          style={{
            fontSize: compactPanel ? 38 : 44,
            lineHeight: 1,
            filter: displayLampOn ? 'drop-shadow(0 0 10px rgba(250,204,21,0.9))' : 'grayscale(0.3)',
          }}
        >
          {outputEmoji(output.type)}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: compactPanel ? 13 : 14,
            fontWeight: 800,
            color: 'var(--color-text)',
          }}
        >
          {displayLampOn ? 'Output: ON' : 'Output: OFF'}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, opacity: 0.75, color: 'var(--color-text)' }}>
          Rule: {(logic.type || 'AND').toUpperCase()} (
          {(logic.type || '').toUpperCase() === 'OR' || (logic.type || '').toUpperCase() === 'PARALLEL'
            ? 'any switch can turn it on'
            : 'every switch must be on'}
          )
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: compactPanel ? 6 : 8 }}>
        {!shellLessonDefer ? (
          <button
            type="button"
            style={{ ...primaryBtn, flex: '1 1 140px', minHeight: compactPanel ? 44 : 48 }}
            onClick={runCheck}
            disabled={completed}
          >
            Check
          </button>
        ) : null}
        <button
          type="button"
          style={{
            ...secondaryBtn,
            flex: shellLessonDefer ? '1 1 100%' : '1 1 120px',
            width: shellLessonDefer ? '100%' : undefined,
            minHeight: compactPanel ? 44 : 48,
            fontSize: compactPanel ? 14 : 16,
          }}
          onClick={resetAll}
          disabled={completed}
        >
          {compactPanel ? 'Reset' : 'Reset switches'}
        </button>
      </div>

      {wrongStripVisible ? (
        <div
          style={{
            ...feedbackIncorrect,
            marginTop: compactPanel ? 8 : 10,
            ...(compactPanel
              ? { fontSize: guided.isNarrow ? 12 : 13, lineHeight: 1.45 }
              : {}),
          }}
        >
          Not quite — compare each switch to the goal state.
        </div>
      ) : null}
      {completed ? (
        <div
          style={{
            ...feedbackCorrect,
            marginTop: compactPanel ? 6 : 10,
            fontSize: compactPanel ? 14 : undefined,
          }}
        >
          {content.victory?.message || 'You matched the logic. Nice!'}
        </div>
      ) : null}
    </div>
  );
}
